// src/pages/ClientsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/SideBar';
import AbsoluteNavigation from '../components/AbsoluteNavigation';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import { MdDelete } from 'react-icons/md';
import AddClientModal from '../components/AddClientModal';
import MailboxConnectionBanner from '../components/MailboxConnectionBanner';
import { useMailboxConnection } from '../contexts/NotificationsContext';
import CampaignMaintenanceDialog from '../components/CampaignMaintenanceDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { useCampaignAccess } from '../contexts/CampaignAccessContext';
import { verifyCampaignCreationAccess } from '../utils/campaignLaunchGuard';
import { requestClientOutboxCancellation } from '../utils/clientOutboxCleanup';
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  writeBatch,
  query,
  where,
  updateDoc,
} from 'firebase/firestore';

import '../styles/ClientsPage.css';
import '../styles/ClientsTable.css';
import '../styles/Loading.css';

// A cleanup that stops half way has to say what is still there. The phase the
// callable failed in names the storage it had not finished with; the advisor is
// never left with a console error and a row that vanished anyway.
export const CLEANUP_RESIDUE_BY_PHASE = {
  preflight: 'Nothing was removed.',
  drive_delete: 'The campaign sheet and its Drive folder may still be in Google Drive.',
  firestore_delete: 'Some campaign records are still stored: conversations, notifications, '
    + 'sheet-change history and audit rows.',
  readback: 'The removal could not be confirmed, so some campaign records or Drive files may remain.',
  cleanup: 'Some campaign records or Drive files may still be stored.'
};

export function describeCleanupFailure({ name, phase, code, message } = {}) {
  const residue = CLEANUP_RESIDUE_BY_PHASE[phase] || CLEANUP_RESIDUE_BY_PHASE.cleanup;
  // The callable's own sentence says WHY (e.g. "Stop this campaign before
  // deleting it"); without it the advisor only sees a phase and a code.
  const reason = String(message || '').trim();
  return `${name || 'This campaign'} was not fully deleted. ${residue} `
    + (reason ? `${reason.replace(/[.]?$/, '.')} ` : '')
    + `The campaign is still listed so you can try again. (${phase || 'cleanup'}/${code || 'cleanup_failed'})`;
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { sessionData, refreshAllData, removeClientFromCache, addClientToCache } = useSessionStorage();
  const mailboxConnection = useMailboxConnection();
  const { refresh: refreshCampaignAccess, maintenanceMessage } = useCampaignAccess();
  const [showAddModal, setShowAddModal] = useState(false);
  const [campaignMaintenance, setCampaignMaintenance] = useState(null);
  const [archivedClients, setArchived] = useState([]);
  const [activeTab, setActiveTab]      = useState('clients');
  const [loading, setLoading] = useState(false);
  const [clientConfirm, setClientConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Use clients from session storage
  const clients = sessionData.clients || [];
  const isLoadingClients = sessionData.clients === null;

  // ─── Thread Cascade Helper Functions ───────────────────────────

  /**
   * Find all threads associated with a client ID
   */
  const findClientThreads = async (clientId) => {
    if (!clientId) return [];

    try {
      const threadsRef = collection(db, 'users', currentUser.uid, 'threads');
      const threadsQuery = query(threadsRef, where('clientId', '==', clientId));
      const threadsSnapshot = await getDocs(threadsQuery);
      
      const matchingThreads = [];
      
      threadsSnapshot.forEach(doc => {
        matchingThreads.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return matchingThreads;
    } catch (error) {
      console.error("❌ Error finding client threads:", error);
      return [];
    }
  };

  /**
   * Archive all thread data for a client (threads, msgIndex, convIndex)
   */
  const archiveClientThreads = async (clientId) => {
    try {
      const threads = await findClientThreads(clientId);
      if (threads.length === 0) {
        console.log(`📭 No threads found for client ${clientId}`);
        return;
      }

      const batch = writeBatch(db);

      for (const thread of threads) {
        const threadId = thread.id;

        // 1) archive thread root
        const archivedThreadRef = doc(db, 'users', currentUser.uid, 'archivedThreads', threadId);
        batch.set(archivedThreadRef, { ...thread, archivedAt: serverTimestamp() });

        // 2) archive thread messages
        const messagesRef = collection(db, 'users', currentUser.uid, 'threads', threadId, 'messages');
        const messagesSnapshot = await getDocs(messagesRef);
        messagesSnapshot.forEach((msgDoc) => {
          const archivedMessageRef = doc(
            db, 'users', currentUser.uid, 'archivedThreads', threadId, 'messages', msgDoc.id
          );
          batch.set(archivedMessageRef, msgDoc.data());
        });

        // 3) archive msgIndex entries for this thread
        const msgIndexRef = collection(db, 'users', currentUser.uid, 'msgIndex');
        const msgIndexQuery = query(msgIndexRef, where('threadId', '==', threadId));
        const msgIndexSnapshot = await getDocs(msgIndexQuery);
        msgIndexSnapshot.forEach((indexDoc) => {
          const indexData = indexDoc.data();
          const archivedIndexRef = doc(db, 'users', currentUser.uid, 'archivedMsgIndex', indexDoc.id);
          batch.set(archivedIndexRef, { ...indexData, clientId, archivedAt: serverTimestamp() });
        });

        // 4) archive convIndex entries for this thread
        const convIndexRef = collection(db, 'users', currentUser.uid, 'convIndex');
        const convIndexQuery = query(convIndexRef, where('threadId', '==', threadId));
        const convIndexSnapshot = await getDocs(convIndexQuery);
        convIndexSnapshot.forEach((convDoc) => {
          const convData = convDoc.data();
          const archivedConvRef = doc(db, 'users', currentUser.uid, 'archivedConvIndex', convDoc.id);
          batch.set(archivedConvRef, { ...convData, clientId, archivedAt: serverTimestamp() });
        });

        // 5) remove live thread + indexes
        await deleteThreadAndSubcollections(threadId);
      }

      await batch.commit();
      console.log(`✅ Archived ${threads.length} threads for client ${clientId}`);
    } catch (error) {
      console.error('❌ Error archiving client threads:', error);
      throw error;
    }
  };


  /**
   * Unarchive all thread data for a client
   */
  const unarchiveClientThreads = async (clientId) => {
    try {
      const batch = writeBatch(db);
      
      // 1. Find and restore archived threads for this client
      const archivedThreadsRef = collection(db, 'users', currentUser.uid, 'archivedThreads');
      const archivedThreadsQuery = query(archivedThreadsRef, where('clientId', '==', clientId));
      const archivedThreadsSnapshot = await getDocs(archivedThreadsQuery);
      
      for (const threadDoc of archivedThreadsSnapshot.docs) {
        const threadId = threadDoc.id;
        const threadData = threadDoc.data();
        
        // Remove archive metadata and restore thread
        const { clientId: _, archivedAt, ...cleanThreadData } = threadData;
        const restoredThreadRef = doc(db, 'users', currentUser.uid, 'threads', threadId);
        batch.set(restoredThreadRef, cleanThreadData);
        
        // Restore messages
        const archivedMessagesRef = collection(db, 'users', currentUser.uid, 'archivedThreads', threadId, 'messages');
        const archivedMessagesSnapshot = await getDocs(archivedMessagesRef);
        
        archivedMessagesSnapshot.forEach(msgDoc => {
          const restoredMessageRef = doc(db, 'users', currentUser.uid, 'threads', threadId, 'messages', msgDoc.id);
          batch.set(restoredMessageRef, msgDoc.data());
        });
      }
      
      // 2. Find and restore archived msgIndex entries for this client
      const archivedMsgIndexRef = collection(db, 'users', currentUser.uid, 'archivedMsgIndex');
      const archivedMsgIndexQuery = query(archivedMsgIndexRef, where('clientId', '==', clientId));
      const archivedMsgIndexSnapshot = await getDocs(archivedMsgIndexQuery);
      
      archivedMsgIndexSnapshot.forEach(indexDoc => {
        const indexData = indexDoc.data();
        const { clientId: _, archivedAt, ...cleanIndexData } = indexData;
        const restoredIndexRef = doc(db, 'users', currentUser.uid, 'msgIndex', indexDoc.id);
        batch.set(restoredIndexRef, cleanIndexData);
      });
      
      // 3. Find and restore archived convIndex entries for this client
      const archivedConvIndexRef = collection(db, 'users', currentUser.uid, 'archivedConvIndex');
      const archivedConvIndexQuery = query(archivedConvIndexRef, where('clientId', '==', clientId));
      const archivedConvIndexSnapshot = await getDocs(archivedConvIndexQuery);
      
      archivedConvIndexSnapshot.forEach(convDoc => {
        const convData = convDoc.data();
        const { clientId: _, archivedAt, ...cleanConvData } = convData;
        const restoredConvRef = doc(db, 'users', currentUser.uid, 'convIndex', convDoc.id);
        batch.set(restoredConvRef, cleanConvData);
      });
      
      await batch.commit();
      
      // 4. Delete archived data after successful restore
      const deleteBatch = writeBatch(db);
      
      archivedThreadsSnapshot.forEach(threadDoc => {
        deleteBatch.delete(threadDoc.ref);
      });
      
      archivedMsgIndexSnapshot.forEach(indexDoc => {
        deleteBatch.delete(indexDoc.ref);
      });
      
      archivedConvIndexSnapshot.forEach(convDoc => {
        deleteBatch.delete(convDoc.ref);
      });
      
      await deleteBatch.commit();
      
      // Delete archived message subcollections
      for (const threadDoc of archivedThreadsSnapshot.docs) {
        const archivedMessagesRef = collection(db, 'users', currentUser.uid, 'archivedThreads', threadDoc.id, 'messages');
        const archivedMessagesSnapshot = await getDocs(archivedMessagesRef);
        
        const messageDeleteBatch = writeBatch(db);
        archivedMessagesSnapshot.forEach(msgDoc => {
          messageDeleteBatch.delete(msgDoc.ref);
        });
        await messageDeleteBatch.commit();
      }
      
      console.log(`✅ Unarchived threads for client ${clientId}`);
      
    } catch (error) {
      console.error("❌ Error unarchiving client threads:", error);
      throw error;
    }
  };

  /**
   * Helper function to delete a thread and all its associated data
   */
  const deleteThreadAndSubcollections = async (threadId) => {
    try {
      const batch = writeBatch(db);

      // 1) delete messages
      const messagesRef = collection(db, 'users', currentUser.uid, 'threads', threadId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      messagesSnapshot.forEach((msgDoc) => batch.delete(msgDoc.ref));

      // 2) delete thread doc
      const threadRef = doc(db, 'users', currentUser.uid, 'threads', threadId);
      batch.delete(threadRef);

      // 3) delete msgIndex entries for this thread
      const msgIndexRef = collection(db, 'users', currentUser.uid, 'msgIndex');
      const msgIndexQuery = query(msgIndexRef, where('threadId', '==', threadId));
      const msgIndexSnapshot = await getDocs(msgIndexQuery);
      msgIndexSnapshot.forEach((indexDoc) => batch.delete(indexDoc.ref));

      // 4) delete convIndex entries for this thread
      const convIndexRef = collection(db, 'users', currentUser.uid, 'convIndex');
      const convIndexQuery = query(convIndexRef, where('threadId', '==', threadId));
      const convIndexSnapshot = await getDocs(convIndexQuery);
      convIndexSnapshot.forEach((convDoc) => batch.delete(convDoc.ref));

      await batch.commit();
    } catch (error) {
      console.error(`❌ Error deleting thread ${threadId}:`, error);
      throw error;
    }
  };


  // Remove the getClientEmails function as it's no longer needed

  // ─── Original Functions (Modified) ──────────────────────────────


  // Client fetching is now handled by SessionStorageContext
  // No need for direct Firestore fetch here

  // Fetch archived clients
  useEffect(() => {
    if (!currentUser) return;
    getDocs(collection(db, 'users', currentUser.uid, 'archivedClients'))
      .then(snapshot => {
        // Filter out any archived clients flagged for deletion
        const archivedList = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => !c.pendingDelete);
        setArchived(archivedList);
      });
  }, [currentUser]);

  const handleAdd = async () => {
    const access = await verifyCampaignCreationAccess(refreshCampaignAccess);
    if (!access.allowed) {
      setCampaignMaintenance(access.message || maintenanceMessage);
      return;
    }
    setShowAddModal(true);
  };

  // Refresh clients from backend after add
  const handleAddSave = (newClient) => {
    if (newClient) {
      addClientToCache(newClient);
    }
    refreshAllData();
    // Navigate to dashboard after creating new client
    navigate('/dashboard');
  };

  async function handleArchive(id) {
    const client = clients.find(c => c.id===id);
    if (!client) return;

    setLoading(true);

    try {
      // Delete pending outbox items (don't send emails for archived client)
      await requestClientOutboxCancellationForClient(id, 'client_archived');

      // Archive client threads using client ID
      await archiveClientThreads(id);

      // Archive the client
      await setDoc(
        doc(db,'users', currentUser.uid, 'archivedClients', id),
        { ...client, archivedAt: serverTimestamp() }
      );
      await deleteDoc(
        doc(db,'users', currentUser.uid, 'clients', id)
      );

      removeClientFromCache(id);
      setArchived(prev => [...prev, { ...client, id }]);

      console.log(`✅ Successfully archived client ${client.name} and their threads`);
      
    } catch (error) {
      console.error("❌ Failed to archive client:", error);
      alert("Failed to archive client and threads. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleUnarchive(id) {
    const client = archivedClients.find(c => c.id === id);
    if (!client) return;

    setLoading(true);

    try {
      // 1) Restore threads first so indexes exist before the client shows up
      await unarchiveClientThreads(id);

      // 2) Move archived client doc back to clients, preserving all fields
      const archivedRef = doc(db, 'users', currentUser.uid, 'archivedClients', id);
      const snap = await getDoc(archivedRef);
      if (!snap.exists()) throw new Error('Archived client doc not found');

      const archivedData = snap.data();
      const { archivedAt, ...restoredPayload } = archivedData; // keep everything else

      await setDoc(
        doc(db, 'users', currentUser.uid, 'clients', id),
        {
          ...restoredPayload,
          // optional audit:
          // unarchivedAt: serverTimestamp(),
          // ensure createdAt is present if you want:
          createdAt: restoredPayload.createdAt || serverTimestamp(),
        },
        { merge: false } // overwrite with the restored payload
      );

      // 3) Delete the archived copy
      await deleteDoc(archivedRef);

      // 4) Update UI state (preserve all fields)
      setArchived(prev => prev.filter(c => c.id !== id));
      addClientToCache({ id, ...restoredPayload });

      console.log(`✅ Successfully unarchived client ${client.name} and their threads`);
    } catch (error) {
      console.error('❌ Failed to unarchive client:', error);
      alert('Failed to unarchive client and threads. Please try again.');
    } finally {
      setLoading(false);
    }
  }


  /**
   * Ask the worker to cancel every pending outbox item for a client.
   */
  const requestClientOutboxCancellationForClient = async (clientId, cancellationReason = 'client_deleted') => {
    try {
      const cancellationRequestedCount = await requestClientOutboxCancellation({
        uid: currentUser.uid,
        clientId,
        cancellationReason
      });

      if (cancellationRequestedCount === 0) {
        console.log(`📭 No pending outbox items found for client ${clientId}`);
        return 0;
      }

      console.log(`✅ Requested cancellation for ${cancellationRequestedCount} outbox items for client ${clientId}`);
      return cancellationRequestedCount;

    } catch (error) {
      console.error("❌ Error deleting client outbox:", error);
      return 0;
    }
  };

  const canDeleteCampaign = client => (
    String(client?.status || '').trim().toLowerCase() === 'stopped'
    && client?.stopKind === 'terminal_stop'
  );

  // The marker that survives a reload. `pendingDelete` is deliberately NOT used:
  // that field hides the campaign from the dashboard cache
  // (SessionStorageContext) and from the archived list, which is exactly the
  // "the row vanished and the trash stayed" behaviour this fix removes.
  const markCleanupIncomplete = async (client, sourceCollection, failure) => {
    if (!currentUser || !client?.id) return;
    try {
      await updateDoc(doc(db, 'users', currentUser.uid, sourceCollection, client.id), {
        cleanupStatus: 'incomplete',
        cleanupFailure: { code: failure.code, phase: failure.phase, ...(failure.message ? { message: failure.message } : {}) },
        cleanupFailedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ Could not record the incomplete cleanup:', error);
    }
  };

  const performCampaignDelete = async (client, sourceCollection, { silentWhenBlocked = false } = {}) => {
    if (!client) return;
    setLoading(true);
    setDeleteError(null);
    try {
      const invokeDeleteCampaign = httpsCallable(functions, 'deleteCampaign');
      const response = await invokeDeleteCampaign({ clientId: client.id });
      if (response?.data?.status !== 'deleted') throw new Error('cleanup_incomplete');
      await refreshAllData();
      if (sourceCollection === 'archivedClients') {
        setArchived(previous => previous.filter(candidate => candidate.id !== client.id));
      } else {
        removeClientFromCache(client.id);
      }
    } catch (error) {
      const code = String(error?.code || 'cleanup_failed').replace(/^functions\//, '');
      const phase = String(error?.details?.phase || 'cleanup');
      const message = code === 'cleanup_failed' && !error?.details ? '' : String(error?.message || '');
      await markCleanupIncomplete(client, sourceCollection, { code, phase, message });
      if (!silentWhenBlocked) setDeleteError({ client, sourceCollection, code, phase, message });
    } finally {
      setLoading(false);
    }
  };

  // A cleanup interrupted by a reload or a closed tab resumes here, so residue
  // is never left behind silently waiting for someone to notice it.
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    const resumeInterruptedCleanups = async () => {
      for (const sourceCollection of ['clients', 'archivedClients']) {
        try {
          const collectionRef = collection(db, 'users', currentUser.uid, sourceCollection);
          const [incomplete, legacyPending] = await Promise.all([
            getDocs(query(collectionRef, where('cleanupStatus', 'in', ['running', 'incomplete']))),
            getDocs(query(collectionRef, where('pendingDelete', '==', true)))
          ]);
          const pending = new Map();
          for (const snapshot of [incomplete, legacyPending]) {
            (snapshot?.docs || []).forEach(candidate => {
              pending.set(candidate.id, { id: candidate.id, ...candidate.data() });
            });
          }
          for (const client of pending.values()) {
            if (cancelled) return;
            await performCampaignDelete(client, sourceCollection, { silentWhenBlocked: false });
          }
        } catch (error) {
          console.error('❌ Could not check for interrupted campaign cleanups:', error);
        }
      }
    };

    resumeInterruptedCleanups();
    return () => { cancelled = true; };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { key:'clients',  label:'Clients',  count:clients.length },
    { key:'archived', label:'Archived', count:archivedClients.length }
  ];

  const requestArchive = (client) => setClientConfirm({
    title: `Archive ${client.name}?`,
    description: 'This removes the campaign from the dashboard and moves its email threads to archived storage.',
    confirmLabel: 'Archive',
    variant: 'warning',
    onConfirm: () => handleArchive(client.id)
  });

  const requestUnarchive = (client) => setClientConfirm({
    title: `Unarchive ${client.name}?`,
    description: 'This restores the campaign and its email threads to the dashboard.',
    confirmLabel: 'Unarchive',
    variant: 'warning',
    onConfirm: () => handleUnarchive(client.id)
  });

  // The confirmation wording is the wording the owner read and approved during
  // the R16 walkthrough; it stays word for word.
  const requestDeleteActive = (client) => {
    if (!canDeleteCampaign(client)) return;
    setClientConfirm({
      title: `Delete ${client.name}?`,
      description: 'This permanently deletes the client and all email threads. This cannot be undone.',
      confirmLabel: 'Delete',
      onConfirm: () => performCampaignDelete(client, 'clients')
    });
  };

  const requestDeleteArchived = (client) => {
    if (!canDeleteCampaign(client)) return;
    setClientConfirm({
      title: `Permanently delete ${client.name}?`,
      description: 'This deletes the archived client and all archived email threads. This cannot be undone.',
      confirmLabel: 'Delete Permanently',
      onConfirm: () => performCampaignDelete(client, 'archivedClients')
    });
  };

  return (
    <div className="app-layout">
      <Sidebar clientCount={clients.length} isLoadingClients={isLoadingClients} />
      <main className="main-content clients-page"
      >
        <AbsoluteNavigation />
        <div className="clients-page__header">
          <h2>Manage Clients</h2>
        </div>
        <MailboxConnectionBanner {...mailboxConnection} />
        {showAddModal && (
          <AddClientModal
            existingNames={[...clients, ...archivedClients].map(c => c.name)}
            onSave={handleAddSave}
            onClose={() => setShowAddModal(false)}
          />
        )}
        <CampaignMaintenanceDialog
          isOpen={Boolean(campaignMaintenance)}
          message={campaignMaintenance || maintenanceMessage}
          onClose={() => setCampaignMaintenance(null)}
        />
        {deleteError && (
          <div className="clients-page__delete-error" role="alert">
            <span>{describeCleanupFailure({
              name: deleteError.client?.name,
              phase: deleteError.phase,
              code: deleteError.code,
              message: deleteError.message
            })}</span>
            <button
              type="button"
              className="btn btn--remove"
              disabled={loading}
              onClick={() => performCampaignDelete(deleteError.client, deleteError.sourceCollection)}
            >
              Retry deletion
            </button>
          </div>
        )}
        <div className="clients-page__table-wrapper">
          <div className="clients-page__tabs">
            {tabs.map(tab => (
              <button
                key={tab.key}
                className={
                  `clients-page__tab` +
                  (activeTab===tab.key ? ' clients-page__tab--active' : '')
                }
                onClick={()=>setActiveTab(tab.key)}
              >
                {tab.label}
                <span className="clients-page__badge">{tab.count}</span>
              </button>
            ))}
            <button
              className={`btn btn--add${clients.length === 0 ? ' btn--pulse' : ''}`}
              onClick={handleAdd}
            >
              Add Client
            </button>
          </div>

          {activeTab==='clients' && isLoadingClients && (
            <table className="clients-table small-table clients-table--loading">
              <thead>
                <tr><th>Client Name</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {[1, 2, 3].map(i => (
                  <tr key={i} className="skeleton-row">
                    <td><div className="skeleton skeleton--text skeleton--name"></div></td>
                    <td className="action-btns">
                      <div className="skeleton skeleton--badge" style={{ width: '60px', height: '28px' }}></div>
                      <div className="skeleton skeleton--circle" style={{ width: '28px', height: '28px' }}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {activeTab==='clients' && !isLoadingClients && clients.length===0 && (
            <p className="clients-page__empty">
              No clients found. Add one using the button above.
            </p>
          )}
          {activeTab==='clients' && !isLoadingClients && clients.length>0 && (
            <table className="clients-table small-table">
              <thead>
                <tr><th>Client Name</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td className="action-btns">
                      <button
                        className="btn btn--archive"
                        onClick={() => requestArchive(c)}
                      >
                        Archive
                      </button>
                      <button
                        className="btn btn--remove"
                        onClick={() => requestDeleteActive(c)}
                        aria-label={`Delete ${c.name}`}
                        title={canDeleteCampaign(c) ? `Delete ${c.name}` : 'Stop this campaign before deleting it'}
                        disabled={loading || !canDeleteCampaign(c)}
                      >
                        <MdDelete size={20}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab==='archived' && archivedClients.length===0 && (
            <p className="clients-page__empty">
              No archived clients.
            </p>
          )}
          {activeTab==='archived' && archivedClients.length>0 && (
            <table className="clients-table small-table">
              <thead>
                <tr><th>Client Name</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {archivedClients.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td className="action-btns">
                      <button
                        className="btn btn--archive"
                        onClick={() => requestUnarchive(c)}
                      >
                        Unarchive
                      </button>
                      <button
                        className="btn btn--remove"
                        onClick={() => requestDeleteArchived(c)}
                        aria-label={`Permanently delete ${c.name}`}
                        title={canDeleteCampaign(c) ? `Permanently delete ${c.name}` : 'Only a stopped campaign can be permanently deleted'}
                        disabled={loading || !canDeleteCampaign(c)}
                      >
                        <MdDelete size={20}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </main>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing client and email threads...</p>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(clientConfirm)}
        title={clientConfirm?.title}
        description={clientConfirm?.description}
        confirmLabel={clientConfirm?.confirmLabel}
        variant={clientConfirm?.variant || 'danger'}
        isLoading={loading}
        onCancel={() => setClientConfirm(null)}
        onConfirm={async () => {
          const action = clientConfirm;
          setClientConfirm(null);
          await action?.onConfirm?.();
        }}
      />
    </div>
  );
}
